<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import {
  useStore,
  useFunctionGetter,
  useMapGetter,
} from 'dashboard/composables/store';

import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import BusinessDay from 'dashboard/routes/dashboard/settings/inbox/components/BusinessDay.vue';
import {
  timeSlotParse,
  timeSlotTransform,
  defaultTimeSlot,
  timeZoneOptions,
} from 'dashboard/routes/dashboard/settings/inbox/helpers/businessHour';

// this fork ships for a Brazilian product, so an untouched team (timezone column
// defaults to UTC, which the timezone list does not offer) starts on Brasilia
const FALLBACK_TIMEZONE = 'America/Sao_Paulo';

const route = useRoute();
const store = useStore();
const { t } = useI18n();

const dayNames = computed(() => [
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.SUNDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.MONDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.TUESDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.WEDNESDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.THURSDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.FRIDAY'),
  t('TEAMS_SETTINGS.WORKING_HOURS.DAYS.SATURDAY'),
]);

const teamId = computed(() => route.params.teamId);
const team = useFunctionGetter('teams/getTeam', teamId);
const uiFlags = useMapGetter('teams/getUIFlags');

const isWorkingHoursEnabled = ref(false);
const timeZone = ref(FALLBACK_TIMEZONE);
const timeSlots = ref([...defaultTimeSlot]);

const timeZones = computed(() => timeZoneOptions());

const hasError = computed(() => {
  if (!isWorkingHoursEnabled.value) return false;
  return timeSlots.value.some(slot => slot.from && !slot.valid);
});

const setDefaults = () => {
  const {
    working_hours_enabled: enabled = false,
    working_hours: workingHours = [],
    timezone: teamTimeZone,
  } = team.value ?? {};

  const parsedSlots = timeSlotParse(workingHours);

  isWorkingHoursEnabled.value = enabled;
  timeSlots.value = parsedSlots.length ? parsedSlots : [...defaultTimeSlot];
  timeZone.value = timeZones.value.some(tz => tz.value === teamTimeZone)
    ? teamTimeZone
    : FALLBACK_TIMEZONE;
};

watch(team, setDefaults, { immediate: true, deep: true });

// the page can be deep-linked or reloaded, so it cannot rely on the sidebar
// having already populated the teams store
onMounted(() => {
  if (!team.value?.id) store.dispatch('teams/get');
});

const isLoading = computed(() => !team.value?.id && uiFlags.value.isFetching);

const onSlotUpdate = (day, slotData) => {
  timeSlots.value = timeSlots.value.map(slot =>
    slot.day === day ? slotData : slot
  );
};

const updateWorkingHours = async () => {
  try {
    await store.dispatch('teams/update', {
      id: teamId.value,
      working_hours_enabled: isWorkingHoursEnabled.value,
      timezone: timeZone.value,
      working_hours: timeSlotTransform(timeSlots.value),
    });
    useAlert(t('TEAMS_SETTINGS.WORKING_HOURS.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('TEAMS_SETTINGS.WORKING_HOURS.API.ERROR_MESSAGE'));
  }
};
</script>

<template>
  <div class="h-full w-full p-8 col-span-6 overflow-y-auto">
    <Spinner v-if="isLoading" />
    <template v-else>
      <SettingsToggleSection
        v-model="isWorkingHoursEnabled"
        :header="$t('TEAMS_SETTINGS.WORKING_HOURS.TOGGLE_AVAILABILITY')"
        :description="$t('TEAMS_SETTINGS.WORKING_HOURS.TOGGLE_HELP')"
      />

      <template v-if="isWorkingHoursEnabled">
        <div class="flex items-center my-8 py-1">
          <div class="flex-1 h-px bg-n-weak" />
          <span class="text-body-main text-n-slate-11 px-2">
            {{ $t('TEAMS_SETTINGS.WORKING_HOURS.WEEKLY_TITLE') }}
          </span>
          <div class="flex-1 h-px bg-n-weak" />
        </div>

        <SettingsFieldSection
          :label="$t('TEAMS_SETTINGS.WORKING_HOURS.TIMEZONE_LABEL')"
        >
          <ComboBox
            v-model="timeZone"
            :options="timeZones"
            :placeholder="$t('INBOX_MGMT.BUSINESS_HOURS.DAY.CHOOSE')"
            class="[&>div>button]:!bg-n-alpha-black2"
          />
        </SettingsFieldSection>
      </template>

      <form class="flex flex-col" @submit.prevent="updateWorkingHours">
        <div v-if="isWorkingHoursEnabled" class="mt-2 w-full">
          <table
            class="min-w-full table-auto outline outline-1 -outline-offset-1 outline-n-weak rounded-xl"
          >
            <thead>
              <tr class="border-b border-n-weak">
                <th
                  class="py-3 ltr:pl-4 ltr:pr-3 rtl:pl-3 rtl:pr-4 text-start text-heading-3 text-n-slate-12"
                >
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.DAY') }}
                </th>
                <th
                  class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                >
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.AVAILABILITY') }}
                </th>
                <th
                  class="py-3 ltr:pr-3 rtl:pl-3 text-start text-heading-3 text-n-slate-12"
                >
                  {{ $t('INBOX_MGMT.BUSINESS_HOURS.DAY.HOURS') }}
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-n-weak">
              <BusinessDay
                v-for="timeSlot in timeSlots"
                :key="timeSlot.day"
                :day-name="dayNames[timeSlot.day]"
                :time-slot="timeSlot"
                @update="data => onSlotUpdate(timeSlot.day, data)"
              />
            </tbody>
          </table>
        </div>
        <div class="w-full flex justify-end items-center py-4 mt-2">
          <NextButton
            type="submit"
            :label="$t('TEAMS_SETTINGS.WORKING_HOURS.UPDATE')"
            :is-loading="uiFlags.isUpdating"
            :disabled="hasError"
          />
        </div>
      </form>
    </template>
  </div>
</template>
